import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import "./template-4.css";
import { ApiUrl, OrderHubUrl, WaiterHubUrl } from "../../../../consts/api";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch } from "../../../../react-reduxs/configure_store";
import { getCompany } from "../../../../react-reduxs/slices/companies/company_slice";
import { useTranslation } from "react-i18next";
import { getCompanyId } from "../../../../react-reduxs/slices/auth/auth_slice";
import { getTableId } from "../../../../react-reduxs/slices/tables/tableSlice";
import * as signalR from "@microsoft/signalr";
import { Tooltip, Popconfirm, Space, Avatar } from "antd";
import BasketComponent from "../../../../components/baskets/BasketComponent";
import { I18nPagePropertyEnum } from "../../../../i18n/i18n_page_property";
import CompanyTableOrderPage from "../../tables/CompanyTableOrderPage";
import MenuProductServcie from "../../../../services/menu_products/menu_product.service";
import MenuCategoryService from "../../../../services/menu_categories/menu_category.service";
import { setCategoryList } from "../../../../react-reduxs/slices/categories/category_slice";
import {
  getProductSelectedCategoryId,
  setProductList,
} from "../../../../react-reduxs/slices/products/product_slice";
import Template4ProductList from "./Template4ProductList";
import Template4Category from "./Template4Category";
import { setOrder } from "../../../../react-reduxs/slices/orders/order_slice";
import { toast } from "react-toastify";
import { OrderHubEnum } from "../../../../enums/order_hub.enum";
import { OrderService } from "../../../../services/orders/order.service";
import Template4Header from "./Template4header";
import { OrderProductStatusService } from "../../../../services/order_product_statuses/order_product_status.service";
import { OrderProductStatusModel } from "../../../../services/order_product_statuses/models/order_product_status.model";

const Template4 = () => {
  const { t } = useTranslation();
  const dispatch: AppDispatch = useDispatch();
  const companyId = useSelector(getCompanyId);
  const company = useSelector(getCompany);
  const tableId = useSelector(getTableId);
  const selectCategoryId = useSelector(getProductSelectedCategoryId);

  const [orderProductStatusList, setOrderProductStatusList] = useState<
    OrderProductStatusModel[]
  >([]);
  // Servisleri her render'da yeniden oluşturmamak için useMemo kullanımı
  const orderService = useMemo(() => new OrderService(), []);
  const categoryService = useMemo(() => new MenuCategoryService(), []);
  const productService = useMemo(() => new MenuProductServcie(), []);
  const orderProductStatusService = useMemo(
    () => new OrderProductStatusService(),
    [],
  );
  const getOrderProductStatusList = useCallback(async () => {
    const res = await orderProductStatusService.getList();
    if (res) setOrderProductStatusList(res);
  }, [orderProductStatusService]);

  // Hub bağlantısını tutmak için ref kullanıyoruz (re-render'lardan etkilenmez)
  const hubConnectionRef = useRef<signalR.HubConnection | null>(null);
  const isInitialMount = useRef(true);

  // Siparişleri Getir
  const fetchOrder = useCallback(async () => {
    if (companyId === "") return;
    try {
      const orderResponse = await orderService.getOrder(
        companyId,
        tableId ?? "",
      );
      dispatch(setOrder(orderResponse || ({} as any)));
    } catch (error) {
      console.error("OrderService Query Error:", error);
    }
  }, [companyId, tableId, dispatch, orderService]);

  // Verileri Başlat (Kategoriler ve Ürünler)
  const initData = useCallback(async () => {
    try {
      const [categories, products] = await Promise.all([
        categoryService.getListByStatus(companyId),
        productService.getListByStatus(companyId),
        getOrderProductStatusList(),
      ]);
      dispatch(setCategoryList(categories || []));
      dispatch(setProductList(products || []));
    } catch (error) {
      console.error("Init Data Error:", error);
    }
  }, [
    companyId,
    categoryService,
    productService,
    getOrderProductStatusList,
    dispatch,
  ]);

  // SignalR Bağlantısını Kur
  useEffect(() => {
    if (companyId === "") return;

    // Mevcut bağlantı varsa durdur (Temizlik)
    if (hubConnectionRef.current) {
      hubConnectionRef.current.stop();
    }

    const connection = new signalR.HubConnectionBuilder()
      .withUrl(OrderHubUrl(companyId).url)
      .withAutomaticReconnect()
      .configureLogging(signalR.LogLevel.Information)
      .build();

    // Event Dinleyicileri
    connection.on(OrderHubEnum.receiveOrder, (order) => {
      if (order?.order?.tableId === tableId) {
        dispatch(setOrder(order));
      }
    });

    connection.on(OrderHubEnum.removeOrderTable, (tableIdParams) => {
      if (tableIdParams === tableId) {
        dispatch(setOrder({} as any));
      }
    });

    // Bağlantıyı Başlat
    const start = async () => {
      try {
        await connection.start();
        console.log("SignalR: OrderHub Connected");
        await fetchOrder(); // Bağlantı kurulunca ilk siparişi çek
      } catch (err) {
        console.error("SignalR: Connection Error", err);
      }
    };

    start();
    hubConnectionRef.current = connection;

    // Cleanup: Bileşen kapandığında bağlantıyı kopar
    return () => {
      if (hubConnectionRef.current) {
        hubConnectionRef.current.stop();
        hubConnectionRef.current = null;
      }
    };
  }, [companyId, tableId, dispatch, fetchOrder]);

  // Sayfa İlk Açıldığında Verileri Çek
  useEffect(() => {
    if (isInitialMount.current) {
      initData();
      isInitialMount.current = false;
    }
  }, [initData]);

  // Garson Çağırma Fonksiyonu
  const handleCallWaiter = async () => {
    const waiterConnection = new signalR.HubConnectionBuilder()
      .withUrl(WaiterHubUrl(companyId).url)
      .withAutomaticReconnect()
      .build();

    try {
      await waiterConnection.start();
      await waiterConnection.invoke("CallTheWaiter", companyId, tableId);
      toast.success(t(I18nPagePropertyEnum.menuProductWaiter) + " Çağrıldı");
    } catch (error) {
      console.error("Waiter Call Error:", error);
      toast.error("Garson çağrılırken hata oluştu.");
    } finally {
      await waiterConnection.stop();
    }
  };

  return (
    <div>
      {/* <div className="t4-head">
        <div className="t4-header">
          <img
            alt="Company Logo"
            width={150}
            src={
              company?.logo
                ? `${ApiUrl}/${company.logo}`
                : "/images/category.png"
            }
          />
          <div className="t4-header-content">
            <h3 className="t4-header-title">{company?.title}</h3>
            {(company?.orderAtTheTable || company?.waiterCall) && (
              <h6 className="t4-header-tableName"> {company?.tableName}</h6>
            )}
          </div>
        </div>
      
        {selectCategoryId === 0 && <Template4Category />}
      </div> */}
      <Template4Header />
      {selectCategoryId === "" && (
        <div style={{ marginTop: "80px" }}>
          <Template4Category />
        </div>
      )}
      {selectCategoryId !== "" && (
        <div>
          <Template4ProductList />
        </div>
      )}
      <div
        style={{
          position: "fixed",
          zIndex: 10,
          right: 11,
          bottom: 20, // ← ekle
          display: "flex",
          flexDirection: "column",
          justifyContent: "left",
          gap: "20px",
        }}
      >
        {company?.orderAtTheTable && (
          <Tooltip
            placement="left"
            title={t(I18nPagePropertyEnum.templateBasketFloat)}
          >
            <BasketComponent color="#549e83" />
          </Tooltip>
        )}

        {company?.waiterCall && (
          <Popconfirm
            icon={
              <i
                className="fa-duotone fa-user-chef fa-lg"
                style={{ color: "black", paddingRight: 5 }}
              ></i>
            }
            placement="top"
            title={t(I18nPagePropertyEnum.menuProductWaiter)}
            description={t(I18nPagePropertyEnum.menuProductCallTheWaiter)}
            onConfirm={handleCallWaiter}
            okText="Evet"
            cancelText={t(I18nPagePropertyEnum.commonCancel)}
          >
            <Space style={{ cursor: "pointer" }}>
              <Avatar
                style={{
                  backgroundColor: "white",
                  cursor: "pointer",
                  padding: 0,
                  boxShadow:
                    "0 2px 10px rgba(84,158,131,0.18), 0 1px 3px rgba(0,0,0,0.05)",
                  transition: "transform 0.18s ease, box-shadow 0.18s ease",
                  border: `1.5px solid rgba(84, 158, 131, 0.22)`,
                  borderRadius: 14,
                  width: 50,
                  height: 50,
                  fontSize: "25px",
                }}
                shape="square"
                icon={
                  <i
                    className="fa-duotone fa-user-chef fa-lg"
                    style={{ color: "#549e83" }}
                  ></i>
                }
              />
            </Space>
          </Popconfirm>
        )}

        {company?.orderAtTheTable && (
          <Tooltip
            placement="left"
            title={t(I18nPagePropertyEnum.templateTableFloat)}
          >
            <CompanyTableOrderPage
              color="#549e83"
              orderProductStatusList={orderProductStatusList}
            />
          </Tooltip>
        )}
      </div>
    </div>
  );
};

export default Template4;
